import { useSelector } from 'react-redux';
import {
  createConfigSelector,
  getActiveLocaleSelector,
  getHasAuthLoadedSelector,
  getIsLoggedInSelector,
} from '@americanexpress/travel-selectors';
import { useErrorOnNotOkFetchye } from '@americanexpress/fetchye-amex';
import { v4 } from 'uuid';
import { ignoreHeadersByKey } from 'fetchye';
import { locales } from '@americanexpress/conditional-features';
import { MODULE_NAME } from '../constants/modules';
import { useMembershipApi } from './membershipDomainDataHooks';
import { useFeatureFlagWithProperties } from '../thunks/useFeatureFlagWithProperties';
import { PERSONA_API_FF } from '../constants/oneXpConstants';

export const getPersonaAccounts = (membershipApiData) => {
  if (!membershipApiData?.data?.membership) {
    return [];
  }

  const accounts = [];

  membershipApiData.data.membership.forEach((value) => {
    const account = {
      iaCode: value?.demographics?.product.productId,
      isBasic: value?.demographics?.product.isBasic,
    };
    accounts.push(account);
  });

  return accounts;
};

export const fetchyeReporting = ({ key, options, error }) => ({
  moduleID: MODULE_NAME,
  component: 'usePersonaApi',
  fetch: {
    url: key,
    status: error?.status,
    correlationId: options.headers?.correlationId || '',
  },
});

export const usePersonaApi = () => {
  const hasPersona = useFeatureFlagWithProperties(PERSONA_API_FF);

  const guestPersonaApiEndpoint = useSelector(createConfigSelector('personaApiGuest'));
  const userPersonaApiEndpoint = useSelector(createConfigSelector('personaApiUser'));
  const hasAuthLoaded = useSelector(getHasAuthLoadedSelector);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const locale = useSelector(getActiveLocaleSelector);
  const {
    isLoading: isMembershipApiLoading,
    data: membershipApiData,
    error: membershipApiError,
  } = useMembershipApi();

  const cards = getPersonaAccounts(membershipApiData);

  const useUserEndpoint = (hasAuthLoaded && isLoggedIn)
    && (!isMembershipApiLoading && !membershipApiError);

  const defer = (!hasPersona && locale !== locales.EN_US)
    || !hasAuthLoaded
    || isMembershipApiLoading;

  const {
    data,
    isLoading: isFetchyeLoading,
    errors,
  } = useErrorOnNotOkFetchye(
    useUserEndpoint ? userPersonaApiEndpoint : guestPersonaApiEndpoint,
    {
      headers: {
        'Content-Type': 'application/json',
        correlationId: v4(),
        clientId: '684C957199C3BE6C153A778D1986032B',
        locale,
      },
      mapOptionsToKey: ignoreHeadersByKey(['correlationId']),
      defer,
      credentials: 'include',
      ...useUserEndpoint && {
        method: 'POST',
        body: JSON.stringify({
          cards,
        }),
      },
    },
    fetchyeReporting
  );

  const isLoading = isFetchyeLoading
    || isMembershipApiLoading
    || !hasAuthLoaded;

  return {
    data: data?.body,
    isLoading,
    errors,
  };
};
